/**
 * Live scanner — Adobe-Scan-like real-time detection 4 углов листа.
 *
 * Архитектура (по research 2026-05-12):
 *   getUserMedia → <video> → requestVideoFrameCallback каждый кадр
 *   → copy в скрытый canvas (downsample 480×640 для производительности)
 *   → jscanify.findPaperContour() → 4 точки в кадре 480×640
 *   → масштабируем обратно до видео-разрешения
 *   → EMA-сглаживание (alpha=0.35) → отдаём callback
 *   → callback обновляет SVG <polygon>
 *
 * Auto-capture: считаем кадры с дрожью <6px. 12 стабильных подряд → snap.
 *
 * Утечки OpenCV.js Mat() лечатся через jscanify (он сам внутри delete'ит).
 * Наши Mat'ы делаем через withMat() helper.
 */

import { ensureScannerInstance } from './scanner';

export interface Corner {
  x: number;
  y: number;
}

export interface LiveScannerOptions {
  /** HTMLVideoElement куда подаётся видеопоток */
  video: HTMLVideoElement;
  /** Обработчик обновления углов (после EMA). null = лист не найден */
  onQuad: (corners: Corner[] | null) => void;
  /** Авто-капчер сработал — отдаём original-canvas (без EMA, с реальными углами) */
  onAutoCapture: (sourceCanvas: HTMLCanvasElement, corners: Corner[]) => void;
  /** Включён ли auto-capture (можно переключать в runtime) */
  autoCaptureEnabled?: () => boolean;
  /** Каждый N-й кадр обрабатывается (1 = каждый, 2 = через один). Default 2. */
  processEveryNth?: number;
}

export interface LiveScannerHandle {
  /** Остановить loop и почистить ресурсы */
  stop: () => void;
  /** Получить текущий канвас с сырым кадром (для manual capture) */
  getCurrentFrameCanvas: () => HTMLCanvasElement;
}

// EMA-параметры
const EMA_ALPHA = 0.35; // 0 = заморожено, 1 = без сглаживания

// Auto-capture эвристика
const STABLE_FRAMES_NEEDED = 12; // ≈ 0.6 сек при rVFC на 20-30 FPS
const PIXEL_JITTER_MAX = 8;

// Downsample для детекции — на 1080p OpenCV.js тормозит
const DETECT_W = 480;
const DETECT_H = 640;

export async function startLiveScanner(opts: LiveScannerOptions): Promise<LiveScannerHandle> {
  const { video, onQuad, onAutoCapture } = opts;
  const autoEnabled = opts.autoCaptureEnabled || (() => true);
  const processEveryNth = opts.processEveryNth ?? 2;

  // Сырой кадр в полном разрешении (для capture + extract)
  const fullCanvas = document.createElement('canvas');
  const fullCtx = fullCanvas.getContext('2d', { willReadFrequently: true })!;

  // Уменьшенный для детекта (4× быстрее findContours)
  const detectCanvas = document.createElement('canvas');
  detectCanvas.width = DETECT_W;
  detectCanvas.height = DETECT_H;
  const detectCtx = detectCanvas.getContext('2d', { willReadFrequently: true })!;

  let stopped = false;
  let frameCounter = 0;
  let smoothed: Corner[] | null = null;
  let lastDetected: Corner[] | null = null;
  let stableCount = 0;
  let lastCaptureAt = 0;

  // Ждём пока jscanify подгрузится (на первом кадре)
  const scanner = await ensureScannerInstance();

  function loop(_now: DOMHighResTimeStamp, _meta?: VideoFrameCallbackMetadata): void {
    if (stopped) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) {
      // Видео ещё не готово (метаданные не пришли)
      (video as any).requestVideoFrameCallback(loop);
      return;
    }

    // Обновляем full-res canvas (нужен для capture)
    if (fullCanvas.width !== vw) {
      fullCanvas.width = vw;
      fullCanvas.height = vh;
    }
    fullCtx.drawImage(video, 0, 0, vw, vh);

    if (frameCounter++ % processEveryNth === 0) {
      // Скейлим до DETECT_W×DETECT_H — детект быстрее в ~4 раза
      detectCtx.drawImage(video, 0, 0, DETECT_W, DETECT_H);
      const cornersSmall = detectQuadOnCanvas(scanner, detectCanvas);

      if (cornersSmall) {
        // Скейлим обратно в координаты видео
        const sx = vw / DETECT_W;
        const sy = vh / DETECT_H;
        const cornersFull: Corner[] = cornersSmall.map((p) => ({
          x: p.x * sx,
          y: p.y * sy,
        }));

        // EMA-сглаживание
        if (!smoothed) {
          smoothed = cornersFull;
        } else {
          smoothed = cornersFull.map((p, i) => ({
            x: EMA_ALPHA * p.x + (1 - EMA_ALPHA) * smoothed![i].x,
            y: EMA_ALPHA * p.y + (1 - EMA_ALPHA) * smoothed![i].y,
          }));
        }
        onQuad(smoothed);

        // Stability check (с реальными, не сглаженными координатами — лучше реагирует)
        if (autoEnabled() && lastDetected) {
          const jitter = maxCornerDelta(cornersFull, lastDetected);
          if (jitter < PIXEL_JITTER_MAX) {
            stableCount++;
            // Cooldown 2 сек после последнего capture — иначе пользователь
            // не успеет убрать камеру / поднять следующий лист
            if (
              stableCount >= STABLE_FRAMES_NEEDED &&
              Date.now() - lastCaptureAt > 2000
            ) {
              lastCaptureAt = Date.now();
              stableCount = 0;
              // Используем сглаженные углы — visually corresponds to overlay
              const finalCorners = smoothed!.map((p) => ({ x: p.x, y: p.y }));
              onAutoCapture(fullCanvas, finalCorners);
            }
          } else {
            stableCount = 0;
          }
        }
        lastDetected = cornersFull;
      } else {
        // Не нашли — сбросить смягчение
        smoothed = null;
        lastDetected = null;
        stableCount = 0;
        onQuad(null);
      }
    }

    (video as any).requestVideoFrameCallback(loop);
  }

  if (typeof (video as any).requestVideoFrameCallback === 'function') {
    (video as any).requestVideoFrameCallback(loop);
  } else {
    // Firefox / старые WebView: fallback на setInterval
    const id = setInterval(() => loop(performance.now()), 100);
    return {
      stop: () => {
        stopped = true;
        clearInterval(id);
      },
      getCurrentFrameCanvas: () => fullCanvas,
    };
  }

  return {
    stop: () => {
      stopped = true;
    },
    getCurrentFrameCanvas: () => fullCanvas,
  };
}

/**
 * Запустить jscanify.findPaperContour и распарсить контур до 4 точек.
 * Возвращает null если jscanify не нашёл контур.
 *
 * jscanify внутри сам управляет cv.Mat'ами и delete'ит их — нам не надо
 * беспокоиться про leaks тут.
 */
function detectQuadOnCanvas(
  scanner: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  canvas: HTMLCanvasElement,
): Corner[] | null {
  try {
    const contour = scanner.findPaperContour(canvas);
    if (!contour) return null;

    // findPaperContour возвращает cv.Mat — у него data32S
    // Точек должно быть 4 (после approxPolyDP в jscanify)
    const data: Int32Array = contour.data32S;
    if (!data || data.length < 8) {
      // не 4 угла — пропускаем кадр
      try {
        contour.delete?.();
      } catch {
        /* */
      }
      return null;
    }
    const corners: Corner[] = [
      { x: data[0], y: data[1] },
      { x: data[2], y: data[3] },
      { x: data[4], y: data[5] },
      { x: data[6], y: data[7] },
    ];
    try {
      contour.delete?.();
    } catch {
      /* */
    }
    // Sort corners в стабильном порядке (TL, TR, BR, BL) — иначе при дрожании
    // EMA может смешивать координаты разных углов
    return sortCornersClockwiseFromTopLeft(corners);
  } catch (err) {
    // OpenCV throw на проблемных кадрах — игнорим
    console.debug('detectQuadOnCanvas err', err);
    return null;
  }
}

function sortCornersClockwiseFromTopLeft(corners: Corner[]): Corner[] {
  // Сортируем по sum(x+y) — top-left имеет минимум, bottom-right максимум.
  // По diff(x-y) — top-right максимум, bottom-left минимум.
  const sums = corners.map((c) => c.x + c.y);
  const diffs = corners.map((c) => c.x - c.y);
  const tlIdx = sums.indexOf(Math.min(...sums));
  const brIdx = sums.indexOf(Math.max(...sums));
  const trIdx = diffs.indexOf(Math.max(...diffs));
  const blIdx = diffs.indexOf(Math.min(...diffs));
  return [corners[tlIdx], corners[trIdx], corners[brIdx], corners[blIdx]];
}

function maxCornerDelta(a: Corner[], b: Corner[]): number {
  let max = 0;
  for (let i = 0; i < 4; i++) {
    const dx = a[i].x - b[i].x;
    const dy = a[i].y - b[i].y;
    const d = Math.hypot(dx, dy);
    if (d > max) max = d;
  }
  return max;
}

/**
 * Утилита для callers: сделать warp по сглаженным углам.
 * Используется при auto-capture И при manual shutter.
 */
export async function warpByCorners(
  sourceCanvas: HTMLCanvasElement,
  corners: Corner[],
): Promise<Blob> {
  const scanner = await ensureScannerInstance();

  // jscanify.extractPaper ожидает cornerPoints в формате { topLeftCorner, ... }
  const cornerPoints = {
    topLeftCorner: corners[0],
    topRightCorner: corners[1],
    bottomRightCorner: corners[2],
    bottomLeftCorner: corners[3],
  };

  // A4 @ 2x DPI = 1240 × 1754
  const out: HTMLCanvasElement = scanner.extractPaper(sourceCanvas, 1240, 1754, cornerPoints);
  return new Promise<Blob>((resolve, reject) => {
    out.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob fail'))),
      'image/jpeg',
      0.92,
    );
  });
}
